import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Close, Dialog, IconButton, makeStyles, useMediaQuery, useTheme } from 'housecall-ui';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { useRecaptcha } from '../../resources/google/useRecaptcha';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { sentryIdentifyUser } from '../../utils/external-libs/sentry';

import { HomeOwnerFlowContentWrapper } from './HomeOwnerFlowContentWrapper';
import { InitMessage } from './types';
import { useInitV1App } from './useInitV1App';

const useStyles = makeStyles((theme: HCPTheme) => ({
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
}));

export const detectIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

const HomeOwnerFlowDialog = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const { closeButton } = useStyles();
  const { dispatch, actions, state } = useOnlineBookingReducer();
  useInitV1App();

  const isInIframeMode = useMemo(detectIframe, []);

  const allowToCloseModal = isInIframeMode;
  const [isHidden, setIsHidden] = useState(detectIframe);

  useRecaptcha();

  const refreshWidget = useCallback(() => {
    setIsHidden(true);

    setTimeout(() => {
      setIsHidden(false);
    }, 0);
  }, []);

  useEffect(() => {
    if (!isHidden) {
      dispatch(actions.setAppOpened(true));
    }
  }, [actions, dispatch, isHidden]);

  const messageHandler = (ev: MessageEvent<InitMessage>) => {
    if (ev.data === 'hcp:open') {
      setIsHidden(false);
    } else if (typeof ev.data === 'object' && ev.data.type === 'hcp:open') {
      const initParams = ev.data.params;
      if (initParams) {
        dispatch(actions.setInitParams(initParams));
        if (initParams.source === 'customer_portal' && initParams?.contactDetails?.email) {
          sentryIdentifyUser({ email: initParams.contactDetails.email });
        }
      }
      setIsHidden(false);
    }
  };

  useEffect(() => {
    window.addEventListener('message', messageHandler);
    return () => {
      window.removeEventListener('message', messageHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.parent.postMessage('hcp:iframe-loaded', '*');
  }, []);

  const handleClose = useCallback(() => {
    if (allowToCloseModal) {
      if (state.success) {
        dispatch(actions.resetFlowWithCart());
      }
      window.parent.postMessage('hcp:close', '*');
      refreshWidget();
    }
  }, [allowToCloseModal, refreshWidget, state.success, dispatch, actions]);

  return (
    <Dialog open fullScreen={fullScreen} maxWidth="sm" fullWidth>
      {allowToCloseModal ? (
        <IconButton className={closeButton} aria-label="close" onClick={handleClose} data-testid="close-dialog-button">
          <Close size="large" color="inherit" />
        </IconButton>
      ) : null}
      {!isHidden && <HomeOwnerFlowContentWrapper />}
    </Dialog>
  );
};

export default HomeOwnerFlowDialog;
